import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { ShieldCheck, ShieldAlert, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [lockoutMsg, setLockoutMsg] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setLockoutMsg("");
    try {
      await login(email, password, captcha?.id, captcha ? captchaAnswer : undefined);
      toast.success("Bem-vindo(a) ao DigiPonto");
    } catch (err) {
      const data = err.response?.data || {};
      const status = err.response?.status;
      if (data.captcha_required && data.captcha) {
        setCaptcha(data.captcha);
        setCaptchaAnswer("");
        toast.warning(data.detail || "Resolva o captcha para continuar", { duration: 3000 });
      } else if (status === 429 || data.locked) {
        setCaptcha(null); setCaptchaAnswer("");
        setLockoutMsg(data.detail || "IP bloqueado temporariamente");
        toast.error(data.detail || "IP bloqueado", { duration: 5000 });
      } else {
        toast.error(data.detail || "Falha ao entrar");
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen login-bg relative flex items-center justify-center p-6">
      {/* Logo top-left (tamanho original) */}
      <div className="hidden lg:flex items-center gap-3 absolute top-6 left-6 text-white">
        <div className="bg-white rounded-xl p-2 shadow-lg">
          <img src="/digiponto-logo.jpeg" alt="DigiPonto" className="h-10 w-auto object-contain" data-testid="login-logo" />
        </div>
        <div className="text-xs text-slate-300">• Sistema de Ponto Eletrônico •</div>
      </div>

      {/* Copyright bottom-left */}
      <div className="hidden lg:flex absolute bottom-6 left-12 text-xs text-slate-400 items-center gap-3">
        <span>© 2026 DigiPonto — Todos os direitos reservados</span>
      </div>

      {/* Form centralizado */}
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8 space-y-5" data-testid="login-form">
          <div className="lg:hidden text-center mb-4">
            <img src="/digiponto-logo.jpeg" alt="DigiPonto" className="mx-auto h-14 w-auto object-contain" />
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900">Acessar sistema</h2>
            <p className="text-sm text-slate-500 mt-1">Entre com seu e-mail cadastrado.</p>
          </div>
          {lockoutMsg && (
            <div className="rounded-lg border-2 border-red-300 bg-red-50 text-red-800 p-3 text-sm flex items-start gap-2" data-testid="lockout-alert">
              <ShieldAlert size={18} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-bold">IP bloqueado</div>
                <div className="text-xs mt-0.5">{lockoutMsg}</div>
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-700">E-mail</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Senha</label>
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none" />
          </div>
          {captcha && (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="captcha-box">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-amber-900 flex items-center gap-1">
                  <ShieldCheck size={13} /> Verificação de segurança
                </span>
                <button type="button" title="Pedir outro captcha"
                  onClick={async () => {
                    try {
                      const r = await api.get("/auth/captcha");
                      setCaptcha(r.data); setCaptchaAnswer("");
                    } catch { toast.error("Falha ao atualizar captcha"); }
                  }}
                  className="text-amber-900 hover:text-amber-700"><RefreshCw size={12} /></button>
              </div>
              <div className="text-sm font-mono text-slate-800 bg-white rounded px-3 py-2 border border-amber-200" data-testid="captcha-question">
                {captcha.question}
              </div>
              <input data-testid="captcha-answer" type="text" inputMode="numeric" required
                value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)}
                placeholder="Sua resposta"
                className="w-full h-9 px-3 rounded border border-amber-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
              <p className="text-[10px] text-amber-800">💡 Após algumas tentativas falhas, pedimos esta verificação para proteger contra bots.</p>
            </div>
          )}
          <button data-testid="login-submit" disabled={loading}
            className="w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold transition disabled:opacity-50">
            {loading ? "Entrando…" : "Entrar"}
          </button>
          <button type="button" data-testid="btn-forgot-password"
            onClick={() => { setForgotEmail(email); setForgotSent(false); setShowForgot(true); }}
            className="w-full text-center text-sm text-emerald-700 hover:text-emerald-800 hover:underline font-medium transition">
            Esqueci minha senha
          </button>
        </form>

        {showForgot && (
          <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4"
               onClick={() => !forgotLoading && setShowForgot(false)}>
            <div onClick={(e) => e.stopPropagation()}
                 className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4"
                 data-testid="dialog-forgot">
              {!forgotSent ? (
                <>
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                      <Mail size={18} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">Recuperar senha</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        Enviamos um link seguro para seu e-mail. Ele expira em 1 hora.
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">E-mail cadastrado</label>
                    <input type="email" required value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      autoFocus data-testid="input-forgot-email"
                      placeholder="seu@email.com.br"
                      className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none" />
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <button type="button" onClick={() => setShowForgot(false)}
                      disabled={forgotLoading}
                      className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-semibold">
                      Cancelar
                    </button>
                    <button type="button" disabled={forgotLoading || !forgotEmail.includes("@")}
                      data-testid="btn-enviar-reset"
                      onClick={async () => {
                        setForgotLoading(true);
                        try {
                          await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
                          setForgotSent(true);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Falha ao enviar e-mail");
                        } finally { setForgotLoading(false); }
                      }}
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
                      <Mail size={14} />
                      {forgotLoading ? "Enviando…" : "Enviar link"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center py-2">
                    <div className="h-12 w-12 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0 mx-auto mb-3">
                      <Mail size={22} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">Verifique seu e-mail</h3>
                    <p className="text-sm text-slate-600 mt-2">
                      Se <strong className="font-mono">{forgotEmail}</strong> estiver cadastrado,
                      você receberá um link de redefinição em instantes.
                    </p>
                    <p className="text-xs text-slate-500 mt-3">
                      Não esquece de olhar o spam. O link expira em 1 hora.
                    </p>
                  </div>
                  <button type="button" onClick={() => setShowForgot(false)}
                    data-testid="btn-fechar-forgot"
                    className="w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold">
                    Entendi
                  </button>
                </>
              )}
            </div>
          </div>
        )}
    </div>
  );
}
